import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { trackLogin, trackSignUp } from '../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AuthContext = createContext(null);

// Configure axios to always send the token
function getAuthHeaders() {
  const token = localStorage.getItem('propost_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem('propost_token');
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/auth/me`, { headers: getAuthHeaders() });
      setUser(data);
    } catch {
      localStorage.removeItem('propost_token');
      localStorage.removeItem('propost_refresh');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    if (data.access_token) {
      localStorage.setItem('propost_token', data.access_token);
      if (data.refresh_token) localStorage.setItem('propost_refresh', data.refresh_token);
    }
    const u = data.user || data;
    setUser(u);
    trackLogin(u?.role || 'unknown');
    return u;
  };

  const register = async (email, password, name, role = 'executive', termsAccepted = false) => {
    // Lazy import to avoid circular deps during initial module load
    let attribution = {};
    try {
      const mod = await import('../utils/attribution');
      attribution = mod.getStoredAttribution() || {};
    } catch { /* attribution is optional */ }
    const { data } = await axios.post(`${API}/auth/register`, {
      email, password, name, role,
      terms_accepted: termsAccepted,
      terms_version: '2026-04-12',
      attribution,
    });
    if (data.access_token) {
      localStorage.setItem('propost_token', data.access_token);
      if (data.refresh_token) localStorage.setItem('propost_refresh', data.refresh_token);
    }
    const u = data.user || data;
    setUser(u);
    trackSignUp(role);
    return u;
  };

  const googleAuth = async (sessionId) => {
    const { data } = await axios.post(`${API}/auth/google`, { session_id: sessionId });
    if (data.access_token) {
      localStorage.setItem('propost_token', data.access_token);
      if (data.refresh_token) localStorage.setItem('propost_refresh', data.refresh_token);
    }
    const u = data.user || data;
    setUser(u);
    trackLogin(u?.role || 'unknown');
    return u;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: getAuthHeaders() });
    } catch {}
    localStorage.removeItem('propost_token');
    localStorage.removeItem('propost_refresh');
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { headers: getAuthHeaders() });
      setUser(data);
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleAuth, logout, refreshUser, setUser, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

export { getAuthHeaders };
